import { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MealPlan from '../../models/meal-plan';
import MealPlansContext from '../../context/meal-plans-context';
import MealsContext from '../../context/meals-context';

interface PlanCompletionProps {
  plan: MealPlan;
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const PlanCompletion = ({ plan, open, onClose, onComplete }: PlanCompletionProps) => {
  const { completePlan } = useContext(MealPlansContext);
  const mealsContext = useContext(MealsContext);

  const [selectedMealIds, setSelectedMealIds] = useState<string[]>([]);
  const [cookedDate, setCookedDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedMealIds(plan.items.map((item) => item.meal.id));
      setCookedDate(new Date().toISOString().slice(0, 10));
      setError(null);
    }
  }, [open, plan.items]);

  const handleToggleMeal = (mealId: string) => {
    setSelectedMealIds((prev) => (prev.includes(mealId) ? prev.filter((id) => id !== mealId) : [...prev, mealId]));
  };

  const handleSelectAll = () => {
    setSelectedMealIds(plan.items.map((item) => item.meal.id));
  };

  const handleDeselectAll = () => {
    setSelectedMealIds([]);
  };

  const handleComplete = async () => {
    setSaving(true);
    setError(null);
    try {
      await completePlan(plan.id, selectedMealIds, cookedDate);
      await mealsContext.refresh();
      onComplete();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Dokončit plán</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Vyberte jídla, která jste uvařili, a zadejte datum vaření.
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button size="small" onClick={handleSelectAll}>
            Vybrat vše
          </Button>
          <Button size="small" onClick={handleDeselectAll}>
            Odznačit vše
          </Button>
        </Stack>

        <List>
          {plan.items.map((item) => (
            <ListItem key={item.id} disablePadding>
              <ListItemButton onClick={() => handleToggleMeal(item.meal.id)} dense>
                <ListItemIcon>
                  <Checkbox edge="start" checked={selectedMealIds.includes(item.meal.id)} tabIndex={-1} disableRipple />
                </ListItemIcon>
                <ListItemText primary={item.meal.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <TextField
          label="Datum vaření"
          type="date"
          value={cookedDate}
          onChange={(e) => setCookedDate(e.target.value)}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Zrušit
        </Button>
        <Button onClick={handleComplete} variant="contained" disabled={saving}>
          {saving ? 'Dokončuji...' : 'Dokončit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanCompletion;
